/**
 * KAN-834: данные приложения менеджера: блок "Требуют внимания" (все каналы),
 * "Мои лиды" (клиенты моей записи менеджера), строка внимания клиента.
 * KAN-929: звонки клиента теперь в ленте переписки (useClientCalls).
 * Ключ внимания вложен в ATTENTION_QUERY_KEY: любое действие со строкой
 * (дашборд, CRM, приложение) обновляет и этот список.
 * KAN-851: оба списка фильтруются по теплоте лида чипами (на клиенте).
 */
import { useCallback, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { clientsApi } from '@/services/api/clients';
import { dashboardApi } from '@/services/api/dashboard';
import { useLeadTemperatureFilter } from '@/hooks/useLeadTemperatureFilter';
import type { AttentionItem } from '@/services/api/dashboard';
import { ATTENTION_QUERY_KEY } from '@/services/api/queryKeys';
import { ALL_LIMIT } from '@/components/dashboard/useAttentionList';
import {
  MOBILE_LEADS_TAB_PARAM,
  MOBILE_LEADS_TABS,
  mobileLeadsFilters,
  mobileLeadsLevelOf,
  mobileLeadsQueryKey,
  parseMobileLeadsTab,
  sortMobileLeads,
  type MobileLeadsTab,
} from '@/lib/mobileLeadsTabs';

export const MOBILE_ATTENTION_QUERY_KEY = [...ATTENTION_QUERY_KEY, 'mobile', ALL_LIMIT] as const;

const attentionLevel = (item: AttentionItem) => item.lead_temperature;

export function useMobileAttention() {
  const query = useQuery({
    queryKey: MOBILE_ATTENTION_QUERY_KEY,
    queryFn: () => dashboardApi.getAttention({ limit: ALL_LIMIT }),
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const all = useMemo(() => query.data?.items ?? [], [query.data]);
  const temperature = useLeadTemperatureFilter(all, attentionLevel);
  return { ...query, queryKey: MOBILE_ATTENTION_QUERY_KEY, all, items: temperature.filtered, temperature };
}

/**
 * Мои лиды: managerId из /auth/me; без записи менеджера запросы не уходят.
 * KAN-897: вкладки "В работе" / "Отработано" (вкладка в адресе ?tab=worked),
 * оба списка грузятся сразу: счётчики вкладок из count каждого ответа.
 * KAN-915: третья вкладка "Следующая связь" (?tab=next), так же со счётчиком.
 */
export function useMyLeads(search: string) {
  const { data: user } = useCurrentUser();
  const managerId = user?.manager_id ?? null;
  const [params, setParams] = useSearchParams();
  const tab = parseMobileLeadsTab(params.get(MOBILE_LEADS_TAB_PARAM));
  const queries = useQueries({
    queries: MOBILE_LEADS_TABS.map((each) => ({
      queryKey: mobileLeadsQueryKey(managerId, each, search),
      queryFn: () => clientsApi.list(mobileLeadsFilters(each, managerId as string, search)),
      enabled: !!managerId,
      staleTime: 30_000,
    })),
  });
  const byTab = Object.fromEntries(MOBILE_LEADS_TABS.map((each, i) => [each, queries[i]])) as Record<
    MobileLeadsTab,
    (typeof queries)[number]
  >;
  const query = byTab[tab];
  const all = useMemo(() => sortMobileLeads(tab, query.data?.results ?? []), [tab, query.data]);
  const temperature = useLeadTemperatureFilter(all, mobileLeadsLevelOf(tab));
  const setTab = useCallback(
    (next: MobileLeadsTab) =>
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          if (next === 'work') copy.delete(MOBILE_LEADS_TAB_PARAM);
          else copy.set(MOBILE_LEADS_TAB_PARAM, next);
          return copy;
        },
        { replace: true }
      ),
    [setParams]
  );
  return {
    tab,
    setTab,
    counts: Object.fromEntries(MOBILE_LEADS_TABS.map((each) => [each, byTab[each].data?.count])) as Record<
      MobileLeadsTab,
      number | undefined
    >,
    isLoading: query.isLoading,
    isError: query.isError,
    all,
    leads: temperature.filtered,
    temperature,
    total: query.data?.count ?? 0,
    hasManager: !!managerId,
  };
}

/**
 * KAN-858: строка внимания клиента из того же списка, что вкладка "Внимание"
 * (тот же ключ, кэш общий); null: клиент не ждёт человека.
 */
export function useClientAttentionItem(clientId: string) {
  return useQuery({
    queryKey: MOBILE_ATTENTION_QUERY_KEY,
    queryFn: () => dashboardApi.getAttention({ limit: ALL_LIMIT }),
    staleTime: 30_000,
    enabled: !!clientId,
    select: (data): AttentionItem | null => data.items.find((item) => item.client_id === clientId) ?? null,
  });
}
